import {
  ArrowLeft, Button, Calendar,
  Edit,
  Heart,
  Mail,
  MapPin,
  Modal,
  Phone,
  User,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { InlineLoader } from "../../components/atoms/InlineLoader";
import { AppointmentCard } from "../../components/molecules/AppointmentCard/AppointmentCard";
import { PageHeader } from "../../components/molecules/PageHeader/PageHeader";
import { PatientForm } from "../../components/molecules/PatientForm/PatientForm";
import { PatientFormSchema } from "../../schemas/patient.schema";
import patientApi from "../../services/patient.api";
import { Appointment } from "../../types/appointment.definition";
import { Patient } from "../../types/patient.definition";
import { convertToDateISOString } from "../../utils/date.utils";

export const PatientDetails: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showAppointmentsModal, setShowAppointmentsModal] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);

  const locale = useMemo(() => {
    return i18n.language.startsWith("fr") ? "fr-FR" : "en-US";
  }, [i18n.language]);

  useEffect(() => {
    const fetchPatient = async () => {
      if (!patientId) {
        setError("Patient ID is required");
        return;
      }

      try {
        setError(null);
        setIsLoading(true);
        const patientData = await patientApi.getPatientById(patientId);
        setPatient(patientData);
      } catch (err) {
        console.error("Error fetching patient:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load patient details",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatient();
  }, [patientId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewAppointments = async () => {
    if (!patient?.id) return;

    try {
      setIsLoadingAppointments(true);
      setShowAppointmentsModal(true);
      const patientAppointments = await patientApi.getPatientAppointments(
        patient.id,
      );
      setAppointments(patientAppointments);
    } catch (err) {
      console.error("Error fetching patient appointments:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load patient appointments",
      );
    } finally {
      setIsLoadingAppointments(false);
    }
  };

  const handleEditPatient = async (patientData: PatientFormSchema) => {
    if (!patient || !patient.id) return;

    try {
      setIsUpdating(true);
      // The form sends no password: it can only be changed by the patient
      const updateData = patientData;

      // Build partial payload with only changed fields
      const payload: Partial<Patient> = {};
      const formattedDob = convertToDateISOString(
        patientData.dateOfBirth as string,
      );

      if (updateData.firstName !== patient.firstName)
        payload.firstName = updateData.firstName;
      if (updateData.lastName !== patient.lastName)
        payload.lastName = updateData.lastName;
      if (updateData.civility !== patient.civility)
        payload.civility = updateData.civility;
      if (updateData.phoneNumber !== patient.phoneNumber)
        payload.phoneNumber = updateData.phoneNumber;
      if (formattedDob !== patient.dateOfBirth)
        payload.dateOfBirth = formattedDob;
      if (
        (updateData.bloodType || undefined) !== (patient.bloodType || undefined)
      )
        payload.bloodType = updateData.bloodType;
      if (updateData.roleName !== patient.roleName)
        payload.roleName = updateData.roleName;
      if (
        JSON.stringify(updateData.preferredNotificationMethods) !==
        JSON.stringify(patient.preferredNotificationMethods)
      )
        payload.preferredNotificationMethods =
          updateData.preferredNotificationMethods;
      if (
        JSON.stringify(updateData.locations) !==
        JSON.stringify(patient.locations)
      )
        payload.locations = updateData.locations;
      if (
        JSON.stringify(updateData.emergencyContacts) !==
        JSON.stringify(patient.emergencyContacts)
      )
        payload.emergencyContacts = updateData.emergencyContacts;

      await patientApi.updatePatient(patient.id, payload);

      // Refresh patient data
      const updatedPatient = await patientApi.getPatientById(patient.id);
      setPatient(updatedPatient);
      setShowEditModal(false);
      // MED-1653: there was no feedback after saving.
      toast.success(
        t("profile.patient.updated", {
          name: `${updatedPatient.firstName || ""} ${updatedPatient.lastName || ""}`.trim(),
        }),
      );
    } catch (err) {
      console.error("Error updating patient:", err);
      setError(err instanceof Error ? err.message : "Failed to update patient");
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <InlineLoader />
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="p-6">
        <div className="py-12 text-center">
          <p className="text-lg font-semibold text-red-600">
            {error || "Patient not found"}
          </p>
          <Button variant="ghost" onClick={handleBack} className="mt-4">
            <ArrowLeft className="mr-2 w-4 h-4" />
            {t("common.back")}
          </Button>
        </div>
      </div>
    );
  }

  const fullName = `${patient.firstName || ""} ${patient.lastName || ""
    }`.trim();

  // MED-1653: a birth date is stored as midnight UTC; formatting it in the
  // viewer's timezone showed the day before west of UTC (31 Dec. for 1 Jan.).
  const dateOfBirth = patient.dateOfBirth
    ? new Date(patient.dateOfBirth).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    })
    : t("staff.manage.details.notSpecified");

  return (
    <div className="p-6 space-y-6 min-h-screen bg-neutral-50">
      <PageHeader
        title={t("profile.patient.details.title")}
        description={t("profile.patient.details.description")}
        icon={User}
        actions={
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="mr-2 w-4 h-4" />
            {t("common.back")}
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Patient Overview Card */}
          <div className="p-6 bg-white rounded-xl border shadow-sm border-neutral-200">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center space-x-4">
                <div className="flex justify-center items-center w-16 h-16 rounded-xl bg-ews-primary">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-neutral-900">
                    {fullName || t("profile.patient.unknownPatient")}
                  </h2>
                  {patient.civility && (
                    <p className="text-sm text-neutral-600">
                      {t(`gender.${patient.civility}`)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Patient Details Grid */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Personal Information */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-neutral-900">
                  {t("common.fieldLabels.personalInformation")}
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <User className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                    <div>
                      <p className="text-sm font-medium text-neutral-900">
                        {t("common.fieldLabels.firstName")}{" "}
                        {t("common.fieldLabels.lastName")}
                      </p>
                      <p className="text-sm text-neutral-600">{fullName}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Calendar className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                    <div>
                      <p className="text-sm font-medium text-neutral-900">
                        {t("common.fieldLabels.dateOfBirth")}
                      </p>
                      <p className="text-sm text-neutral-600">{dateOfBirth}</p>
                    </div>
                  </div>

                  {patient.civility && (
                    <div className="flex items-start space-x-3">
                      <User className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {t("common.fieldLabels.gender")}
                        </p>
                        <p className="text-sm text-neutral-600">
                          {t(`gender.${patient.civility}`)}
                        </p>
                      </div>
                    </div>
                  )}

                  {patient.bloodType && (
                    <div className="flex items-start space-x-3">
                      <Heart className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {t("patient.form.fields.bloodType")}
                        </p>
                        <p className="text-sm text-neutral-600">
                          {patient.bloodType}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-neutral-900">
                  {t("common.fieldLabels.contactInformation")}
                </h4>
                <div className="space-y-3">
                  {patient.email && (
                    <div className="flex items-start space-x-3">
                      <Mail className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {t("common.fieldLabels.email")}
                        </p>
                        <p
                          className="text-sm break-words text-neutral-600"
                          style={{ wordBreak: "break-all" }}
                        >
                          {patient.email}
                        </p>
                      </div>
                    </div>
                  )}

                  {patient.phoneNumber && (
                    <div className="flex items-start space-x-3">
                      <Phone className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {t("common.fieldLabels.phoneNumber")}
                        </p>
                        <p className="text-sm text-neutral-600">
                          {formatPhoneNumberForDisplay(patient.phoneNumber)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Legacy location support - show if locations array doesn't exist */}
                  {!patient.locations && patient.location && (
                    <div className="flex items-start space-x-3">
                      <MapPin className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {t("location.address")}
                        </p>
                        <p className="text-sm text-neutral-600">
                          {patient.location.address ||
                            t("profile.patient.notSpecified")}
                          {patient.location.city &&
                            `, ${patient.location.city}`}
                          {patient.location.state &&
                            `, ${patient.location.state}`}
                          {patient.location.zip && ` ${patient.location.zip}`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Locations Section */}
            {patient.locations && patient.locations.length > 0 && (
              <div className="pt-6 mt-6 border-t border-neutral-200">
                <h4 className="mb-4 text-lg font-medium text-neutral-900">
                  {t("location.title")}
                </h4>
                <div className="space-y-4">
                  {patient.locations.map((location, index) => (
                    <div
                      key={location.id || index}
                      className="p-4 rounded-lg border bg-neutral-50 border-neutral-200"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex gap-3 items-center">
                          <h5 className="text-sm font-medium text-neutral-900">
                            {t("location.title")} {index + 1}
                          </h5>
                          {location.isPrimary && (
                            <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium rounded-full border bg-primary-100 text-primary-700 border-primary-200">
                              <span className="text-primary-600">★</span>
                              {t("location.isPrimary")}
                            </span>
                          )}
                          {location.residency && (
                            <span className="px-2 py-1 text-xs font-medium rounded text-neutral-700 bg-neutral-100">
                              {location.residency === "HOME"
                                ? t("location.residency.home")
                                : location.residency === "SECOND_HOME"
                                  ? t("location.residency.secondHome")
                                  : t("location.residency.other")}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {location.address && (
                          <div className="flex items-start space-x-2">
                            <MapPin className="flex-shrink-0 w-4 h-4 mt-0.5 text-neutral-400" />
                            <p className="text-sm text-neutral-600">
                              {location.address}
                            </p>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2 text-sm text-neutral-600">
                          {location.state && <span>{location.state}</span>}
                          {location.city && <span>{location.city}</span>}
                          {location.zip && <span>{location.zip}</span>}
                          {location.country && (
                            <span className="font-medium">
                              {t(
                                `address.countries.${location.country}`,
                                location.country,
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Medical Information */}
            {(patient.allergies ||
              patient.medications ||
              patient.lastVisit) && (
                <div className="pt-6 mt-6 border-t border-neutral-200">
                  <h4 className="mb-4 text-lg font-medium text-neutral-900">
                    Medical Information
                  </h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {patient.allergies && (
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          Allergies
                        </p>
                        <p className="text-sm text-neutral-600">
                          {patient.allergies.length > 0
                            ? patient.allergies.join(", ")
                            : "None reported"}
                        </p>
                      </div>
                    )}

                    {patient.medications && (
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          Current Medications
                        </p>
                        <p className="text-sm text-neutral-600">
                          {patient.medications.length > 0
                            ? patient.medications.join(", ")
                            : "None"}
                        </p>
                      </div>
                    )}

                    {patient.lastVisit && (
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          Last Visit
                        </p>
                        <p className="text-sm text-neutral-600">
                          {new Date(patient.lastVisit).toLocaleDateString(locale)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* Emergency Contacts */}
            {patient.emergencyContacts &&
              patient.emergencyContacts.length > 0 && (
                <div className="pt-6 mt-6 border-t border-neutral-200">
                  <h4 className="mb-4 text-lg font-medium text-neutral-900">
                    {t("patient.form.emergencyContact.listTitle")}
                  </h4>
                  <div className="space-y-3">
                    {patient.emergencyContacts.map((contact, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-lg border bg-neutral-50 border-neutral-200"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium text-neutral-900">
                              {contact.firstName} {contact.lastName}
                            </p>
                            <p className="text-sm text-neutral-600">
                              {contact.relationship}
                            </p>
                          </div>
                          <div className="text-right">
                            {contact.phoneNumber && (
                              <p className="text-sm text-neutral-600">
                                {formatPhoneNumberForDisplay(contact.phoneNumber)}
                              </p>
                            )}
                            {contact.email && (
                              <p className="text-sm text-neutral-600">
                                {contact.email}
                              </p>
                            )}
                            {contact.priority === 0 && (
                              <span className="inline-block px-2 py-1 mt-1 text-xs font-medium rounded text-primary-700 bg-primary-100">
                                Primary
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Sidebar - Quick Actions */}
        <div className="space-y-6">
          <div className="p-6 bg-white rounded-xl border shadow-sm border-neutral-200">
            <h3 className="mb-4 text-lg font-semibold text-neutral-900">
              {t("profile.patient.quickActions.title")}
            </h3>
            <div className="space-y-2">
              {patient.id && (
                <Button
                  variant="ghost"
                  className="justify-start w-full"
                  onClick={handleViewAppointments}
                >
                  <Calendar className="mr-2 w-4 h-4" />
                  {t("profile.patient.quickActions.viewAppointments")}
                </Button>
              )}

              <Button
                variant="ghost"
                className="justify-start w-full"
                onClick={() => setShowEditModal(true)}
              >
                <Edit className="mr-2 w-4 h-4" />
                {t("profile.patient.quickActions.editPatient")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Patient Modal */}
      {showEditModal && patient && (
        <PatientForm
          onSubmit={handleEditPatient}
          onCancel={() => setShowEditModal(false)}
          isSubmitting={isUpdating}
          initialData={patient}
          isEditMode={true}
        />
      )}

      {/* Patient Appointments Modal */}
      {showAppointmentsModal && (
        <Modal
          isOpen
          onClose={() => setShowAppointmentsModal(false)}
          title={t("profile.patient.appointmentsModal.title")}
          variant="info"
          size="4xl"
          primaryAction={t("profile.patient.appointmentsModal.close")}
          onPrimaryAction={() => setShowAppointmentsModal(false)}
        >
          <p className="mb-6 -mt-2 text-sm text-neutral-600">
            {fullName || t("profile.patient.unknownPatient")}
          </p>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                {isLoadingAppointments ? (
                  <InlineLoader
                    message={t("profile.patient.appointmentsModal.loading")}
                  />
                ) : appointments.length > 0 ? (
                  appointments.map((appointment) => (
                    <AppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      isUpdating={false}
                      onStatusChange={() => {
                        // Refresh appointments after status change
                        if (patient?.id) {
                          patientApi
                            .getPatientAppointments(patient.id)
                            .then(setAppointments)
                            .catch(console.error);
                        }
                      }}
                    />
                  ))
                ) : (
                  <div className="py-8 text-center text-neutral-500">
                    <Calendar className="mx-auto mb-4 w-12 h-12 text-neutral-300" />
                    <p>{t("profile.patient.appointmentsModal.noAppointments")}</p>
                  </div>
                )}
          </div>
        </Modal>
      )}
    </div>
  );
};
